import { fetchToApi, queryClient } from "@data/lib";
import { useAuthStore } from "@mod/auth";
import { AppRole, rolesInitialValue } from "@mod/auth/types";
import { useMutation } from "@tanstack/react-query";
import deepmerge from "deepmerge";
import { useMemo } from "react";
import { User } from "../modules/auth/types";
import { useApi } from "./api";
import { AuthenticationResponseType } from "./types";

export const CHANGE_PASSWORD_QUERY_KEY = "change-password";

export const USERS_QUERY_KEY = "users";
export const USERS_LIST_QUERY_KEY = "users-list";
export const AUTHENTICATION_QUERY_KEY = "authentication";
export const SIGNIN_SERVER_QUERY_KEY = "signin-server";
export const UPDATE_USER_QUERY_KEY = "update-user";
export const CREATE_USER_QUERY_KEY = "create-user";
export const UPDATE_USER_PASSWORD_QUERY_KEY = "update-user-password";
export const FORGOT_PASSWORD_QUERY_KEY = "forgot-password";
export const RESET_PASSWORD_QUERY_KEY = "reset-password";

export const useAuthentication = () => {
  const { mutateAsync, isPending, data } = useApi(AUTHENTICATION_QUERY_KEY).useApiMutation<AuthenticationResponseType>({
    url: "/auth",
    method: "POST"
  });

  return {
    authenticate: mutateAsync,
    loading: isPending,
    response: data?.data ?? null
  };
};

export const useSignInServer = () => {
  const { mutateAsync, isPending, data } = useApi(SIGNIN_SERVER_QUERY_KEY).useApiMutation<ResponseType>({
    url: "/signin-by-server-email",
    method: "POST"
  });

  return {
    signInServer: mutateAsync,
    loading: isPending,
    response: data
  };
};

export const useDataUser = () => useApi(USERS_QUERY_KEY).useSecuredApiCall<User>({ url: "/v1/user/user" });

export const useUserList = () => {
  const { data, isFetching } = useApi(USERS_LIST_QUERY_KEY).useSecuredApiCall<User[]>({
    url: "/v1/user/get-user-list"
  });

  const {
    mutateAsync,
    isPending,
    data: userData
  } = useApi(UPDATE_USER_QUERY_KEY).useApiMutation<AuthenticationResponseType>({
    url: "/v1/user/update-user",
    method: "PUT",
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [USERS_LIST_QUERY_KEY, USERS_QUERY_KEY]
      });
    }
  });

  const users = data?.data ?? [];

  users.sort((a, b) => (a.isAdmin ? -1 : 1 || (a.displayName ?? "").localeCompare(b.displayName ?? "")));

  return { users, isFetching, update: mutateAsync, isUpdating: isPending, userData };
};

export const useCreateUser = () => {
  const { mutateAsync, isPending, data } = useApi(CREATE_USER_QUERY_KEY).useApiMutation<AuthenticationResponseType>({
    url: "/v1/user/create-user",
    method: "POST",
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [USERS_LIST_QUERY_KEY]
      });
    }
  });

  return { create: mutateAsync, loading: isPending, response: data?.data ?? null };
};

export const useUpdateUserPassword = () => {
  const { mutateAsync, isPending, data } = useApi(UPDATE_USER_PASSWORD_QUERY_KEY).useApiMutation({
    url: "/v1/user/set-user-password",
    method: "PUT"
  });

  return { updatePassword: mutateAsync, loading: isPending, response: data?.data ?? null };
};

export const useAuthUser = () => {
  const { data: userData, isFetching } = useDataUser();

  const userRoles: AppRole = useMemo(
    () =>
      deepmerge(
        rolesInitialValue,
        (userData?.data?.roles ?? []).reduce((acc, role: any) => {
          acc[role as keyof AppRole] = true;
          return acc;
        }, {} as Partial<AppRole>)
      ),
    [userData]
  );

  const userAssgiendRoles = useMemo(
    () => ({
      userData: userData?.data,
      isFetching,
      hasAuthRole: userRoles.auth,
      isAdmin: !!userData?.data?.isAdmin,
      userRoles,
      hasViewEventsAccess: userRoles["view-events"],
      hasEditEventsAccess: userRoles["edit-events"],
      hasEditGroupAccess: userRoles["edit-groups"],
      /// guest
      hasEditGuestsAccess: userRoles["edit-guests"],
      hasRemoveGuestAccess: userRoles["remove-guest"],
      hasEditPaymentsAccess: userRoles["edit-payments"],
      hasViewPaymentsAccess: userRoles["view-payments"],
      hasRemovePaymentsAccess: userRoles["remove-payments"],
      hasTransferGuestsAccess: userRoles["transfer-guests"],
      // workshops
      hasViewWorkshopsAccess: userRoles["view-workshops"],
      hasEditWorkshopsAccess: userRoles["edit-workshops"],
      // rooms
      hasViewRoomsAccess: userRoles["view-rooms"],
      hasEditRoomsAccess: userRoles["edit-rooms"],
      // dashboard
      hasViewDashboardAccess: userRoles["view-dashboard"],
      // services
      hasViewServicesAccess: userRoles["view-services"],
      hasEditServicesAccess: userRoles["edit-services"],
      hasEditMeetingsAccess: userRoles["edit-meetings"],
      // invitations
      hasViewInvitationsAccess: userRoles["view-invitations"],
      hasEditInvitationsAccess: userRoles["edit-invitations"],
      // comissions
      hasViewComissionsAccess: userRoles["view-comissions"],
      hasEditComissionsAccess: userRoles["edit-comissions"],
      // commitments
      hasViewCommitmentsAccess: userRoles["view-commitments"],
      hasEditCommitmentsAccess: userRoles["edit-commitments"],
      // commitment servers
      hasViewCommitmentServersAccess: userRoles["view-commitment-servers"],
      hasEditCommitmentServersAccess: userRoles["edit-commitment-servers"],
      // commitment payments
      hasViewCommitmentPaymentsAccess: userRoles["view-commitment-payments"],
      hasEditCommitmentPaymentsAccess: userRoles["edit-commitment-payments"],
      hasRemoveCommitmentPaymentsAccess: userRoles["remove-commitment-payments"],
      hasViewEventPaymentsAccess: userRoles["view-event-payments"],
      // prayer requests
      hasViewPrayerRequestsAccess: userRoles["view-prayer-requests"],
      hasEditPrayerRequestsAccess: userRoles["edit-prayer-requests"],
      // preaching
      hasViewPreachingAccess: userRoles["view-preaching"],
      hasEditPreachingAccess: userRoles["edit-preaching"],
      hasViewPreachingEventsAccess: userRoles["view-preaching-events"],
      hasEditPreachingEventsAccess: userRoles["edit-preaching-events"],
      hasViewPlanningMeetingsAccess: userRoles["view-planning-meetings"],
      hasEditPlanningMeetingsAccess: userRoles["edit-planning-meetings"],
      hasGeneratePreachingAssignmentsAccess: userRoles["generate-preaching-assignments"],
      hasViewPreachingAssignmentsAccess: userRoles["view-preaching-assignments"],
      hasEditPreachingAssignmentsAccess: userRoles["edit-preaching-assignments"],
      hasPreachingLiaisonAccess: userRoles["preaching-liaison"],
      // meeting attendance
      hasViewMeetingAttendanceAccess: userRoles["view-meeting-attendance"],
      hasEditMeetingAttendanceAccess: userRoles["edit-meeting-attendance"],
      hasEditMeetingAttendanceSecondaryAccess: userRoles["edit-meeting-attendance-secondary"],
      // landing
      hasLandingAdminAccess: userRoles["landing-admin"],
      // move participants
      hasMoveParticipantsAccess: userRoles["move-participants"]
    }),
    [userData, userRoles]
  );

  return userAssgiendRoles;
};

export class WrongPasswordError extends Error {
  constructor() {
    super("wrong_password");
    this.name = "WrongPasswordError";
  }
}

export const useChangePassword = () => {
  const { token } = useAuthStore();

  const { mutateAsync, isPending, reset } = useMutation({
    mutationKey: [CHANGE_PASSWORD_QUERY_KEY],
    mutationFn: async ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) => {
      const response = await fetchToApi(
        "/v1/user/change-password",
        "PUT",
        { currentPassword, newPassword },
        token ?? undefined
      );

      if (response.status === 401) {
        throw new WrongPasswordError();
      }

      if (!response.ok) {
        throw new Error("generic_error");
      }

      return response.json();
    }
  });

  return { changePassword: mutateAsync, isPending, reset };
};

export const useForgotPassword = () => {
  const { mutateAsync, isPending, data, reset } = useApi(FORGOT_PASSWORD_QUERY_KEY).useApiMutation({
    url: "/auth/forgot-password",
    method: "POST"
  });

  return { forgotPassword: mutateAsync, isPending, response: data, reset };
};

export class InvalidRecoveryLinkError extends Error {
  constructor() {
    super("invalid_recovery_link");
    this.name = "InvalidRecoveryLinkError";
  }
}

export const useResetPassword = () => {
  const { mutateAsync, isPending } = useMutation({
    mutationKey: [RESET_PASSWORD_QUERY_KEY],
    mutationFn: async ({ accessToken, newPassword }: { accessToken: string; newPassword: string }) => {
      const response = await fetchToApi("/auth/reset-password", "POST", { accessToken, newPassword });

      if (response.status === 401 || response.status === 403) {
        throw new InvalidRecoveryLinkError();
      }

      if (!response.ok) {
        throw new Error("generic_error");
      }

      return response.json();
    }
  });

  return { resetPassword: mutateAsync, isPending };
};
